// Core lib dependencies.
import React from 'react';
import PropTypes from 'prop-types';

// Component dependencies.
import { PagingButton } from '../../../../common/components/buttons.jsx';

// Misc dependencies.
import { getLabel } from '../../../../common/utils/string.js';

// Config.
import Config from '../../config/config.js';
import Enums from '../../config/enums.js';

/**
 * Namespace for UgcWall toolbar components.
 *
 * @memberof module:UgcWall.Components.Elements
 * @namespace Toolbars
 */

/**
 * <pre>
 * UgcWall > Toolbars > Paging Toolbar React component.
 *
 * Summary of responsibilities:
 *
 *  - outputs a toolbar to allow previous/next and continuous paging through DisplayItem resultset.
 * </pre>
 *
 * @memberof module:UgcWall.Components.Elements.Toolbars
 * @namespace
 *
 * @return {module:UgcWall.Components.Elements.Toolbars.PagingToolbar}
 */
class PagingToolbar extends React.Component {
  /**
   * Constructor.
   *
   * @param {object} props The injected props
   */
  constructor(props) {
    super(props);

    // Bind methods.
    this.handleShowMore = this.handleShowMore.bind(this);
    this.handleShowPrevious = this.handleShowPrevious.bind(this);
    this.handleShowNext = this.handleShowNext.bind(this);
  }

  /* - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - */
  /* Render methods */

  /**
   * Render the component. Compose child components as required.
   *
   * @return {module:UgcWall.Components.Elements.Toolbars.PagingToolbar}
   */
  // Render component.
  render() {
    let _buttons = [];
    switch (this.props.showMoreMode) {
      case Enums.SHOW_MORE_MODE_REPLACE:
        const _prevButton = (
          <PagingButton
            key="1"
            onClick={this.handleShowPrevious}
            className="xPagingButton xActionPaginate xActionShowPrevious"
          >
            {getLabel('ugc.paging.previous')}
          </PagingButton>
        );

        const _nextButton = (
          <PagingButton key="2" onClick={this.handleShowNext} className="xPagingButton xActionPaginate xActionShowNext">
            {getLabel('ugc.paging.next')}
          </PagingButton>
        );

        // If we are not on the first page, output the previous button.
        if (this.props.currentPage > 1) {
          _buttons.push(_prevButton);
        }

        if (this.props.currentPage < this.props.totalPages) {
          _buttons.push(_nextButton);
        }

        break;

      case Enums.SHOW_MORE_MODE_APPEND:
      default:
        const hasReachedDisplayContentLimit = this.props.allowAutoPolling && this.props.itemCount >= Enums.CONTENT_CAP;
        // If we have more pages, output the show more button.
        // hide button when auto-polling limit is met
        if (this.props.currentPage < this.props.totalPages && !hasReachedDisplayContentLimit) {
          const _label = this.props.labels.showMore ? this.props.labels.showMore : getLabel('ugc.paging.more');

          _buttons = <PagingButton onClick={this.handleShowMore}>{_label}</PagingButton>;
        }

        break;
    }

    return (
      <div className="xActionContainer xActionShowMoreContainer">
        <div className="xPaginationContainer xPaginateShowMore">{_buttons}</div>
      </div>
    );
  }

  /* - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - */
  /* Event handler methods */

  /**
   * Handler called when show more button is clicked.
   *
   */
  handleShowMore() {
    if (this.props.clickHandlers.showMore) {
      this.props.clickHandlers.showMore();
    }
  }

  /**
   * Handler called when previous button is clicked.
   *
   */
  handleShowPrevious() {
    if (this.props.clickHandlers.showPrevious) {
      this.props.clickHandlers.showPrevious();
    }
  }

  /**
   * Handler called when next button is clicked.
   *
   */
  handleShowNext() {
    if (this.props.clickHandlers.showNext) {
      this.props.clickHandlers.showNext();
    }
  }
}

/**
 * React PropTypes.
 *
 * @type {Object}
 * @property {Integer} currentPage The current page number.
 * @property {Integer} totalPages The total number of pages in the resultset.
 * @property {Integer} itemCount current total number of items fetched.
 * @property {String} showMoreMode String indicating the show more mode (replace|append)
 * @property {Object} clickHandlers Object containing event handlers for various events.
 * @property {Object} labels Object containing varios localized labels.
 * @property {Boolean} allowAutoPolling boolean defining whether display module is auto polling for content
 */
PagingToolbar.propTypes = {
  currentPage:      PropTypes.number,
  totalPages:       PropTypes.number,
  showMoreMode:     PropTypes.string,
  itemCount:        PropTypes.number,
  clickHandlers:    PropTypes.object,
  labels:           PropTypes.object,
  allowAutoPolling: PropTypes.bool,
};

/**
 * React default props.
 *
 * @type {Object}
 */
PagingToolbar.defaultProps = {};

export default PagingToolbar;
